import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getAllOrdersOfUser } from "../../redux/actions/order";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "@mui/material";
import { Link } from "react-router-dom";
import { MdTrackChanges } from "react-icons/md";

const TrackOrderDetails = ({ order }) => {
  if (!order) return null;

  return (
    <div className="w-full py-8 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-6">Order Tracking Details</h2>
        
        {/* Order Status */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-2">Current Status</h3>
          <div className="flex items-center">
            <div className={`w-4 h-4 rounded-full mr-2 ${
              order.status === "Delivered" ? "bg-green-500" : "bg-yellow-500"
            }`}></div>
            <p className="text-lg">
              {order.status === "Processing" 
                ? "Your Order is processing in shop."
                : order.status === "Packed and ready for dispatch" 
                ? "Your Order is packed and ready for dispatch."
                : order.status === "Handed to transporter" 
                ? "Your Order has been handed to the transporter."
                : order.status === "On the way" 
                ? "Our Delivery man is going to deliver your order."
                : order.status === "Delivered" 
                ? "Your order is delivered!"
                : order.status}
            </p>
          </div>
        </div>

        {/* Tracking Reference */}
        {order.customerTrackingReference && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-2">Tracking Reference</h3>
            <div className="bg-gray-100 p-4 rounded-md">
              <p className="text-lg font-mono">{order.customerTrackingReference}</p>
            </div>
          </div>
        )}

        {/* Order Summary */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p><span className="font-medium">Order ID:</span> {order._id}</p>
              <p><span className="font-medium">Order Date:</span> {new Date(order.createdAt).toLocaleDateString()}</p>
              <p><span className="font-medium">Total Items:</span> {order.cart.length}</p>
            </div>
            <div>
              <p><span className="font-medium">Total Amount:</span> ₹{order.shopTotalOrderPriceWithCommission.toFixed(2)}</p>
              {order.deliveredAt && (
                <p><span className="font-medium">Delivered On:</span> {new Date(order.deliveredAt).toLocaleDateString()}</p>
              )}
            </div>
          </div>
        </div>

        {/* Shipping Address */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-2">Shipping Address</h3>
          <div className="bg-gray-100 p-4 rounded-md">
            <p>{order.shippingAddress.address1}</p>
            {order.shippingAddress.address2 && <p>{order.shippingAddress.address2}</p>}
            <p>{order.shippingAddress.city}, {order.shippingAddress.state}</p>
            <p>{order.shippingAddress.country} - {order.shippingAddress.zipCode}</p>
          </div>
        </div>

        {/* Back to Orders */}
        <div className="mt-6">
          <Link to="/profile">
            <Button
              variant="contained"
              color="primary"
              startIcon={<MdTrackChanges />}
            >
              Back to My Orders
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

const TrackOrder = () => {
  const { user } = useSelector((state) => state.user);
  const { orders } = useSelector((state) => state.order);
  const dispatch = useDispatch();
  const { id } = useParams();

  useEffect(() => {
    if (user && user._id) {
      dispatch(getAllOrdersOfUser(user._id));
    }
  }, [dispatch, user]);

  // If we're viewing a specific order (from URL params)
  if (id) {
    const order = orders.find((item) => item._id === id);
    return <TrackOrderDetails order={order} />;
  }

  // Otherwise show the orders list with tracking options
  const columns = [
    { field: "id", headerName: "Order ID", minWidth: 150, flex: 1 },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 1,
      cellClassName: (params) =>
        params.row.status === "Delivered"
          ? "text-green-600 font-semibold"
          : "text-red-600 font-semibold",
    },
    {
      field: "itemsQty",
      headerName: "Items Qty",
      type: "number",
      minWidth: 100,
      flex: 0.7,
    },
    {
      field: "total",
      headerName: "Total",
      type: "number",
      minWidth: 100,
      flex: 0.8,
      renderCell: (params) => <span>₹ {params.value}</span>,
    },
    {
      field: "track",
      headerName: "Track Order",
      minWidth: 150,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Link to={`/user/track/order/${params.id}`}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<MdTrackChanges size={20} />}
            className="flex items-center justify-center"
          >
            Track
          </Button>
        </Link>
      ),
    },
  ];

  const rows = orders
    ? orders.map((item) => ({
        id: item._id,
        itemsQty: item.cart.length,
        total: item.shopTotalOrderPriceWithCommission.toFixed(2),
        status: item.status,
      }))
    : [];

  return (
    <div className="w-full min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">My Orders</h1>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div style={{ minWidth: "100%", height: 500 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              disableSelectionOnClick
              className="data-grid"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackOrder;